import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allShapes, isPlaceholder, readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionSchriftgroessen: Lesson = {
  id: "schriftgroessen",
  title: "Schriftgrößen kommen aus dem Layout",
  summary: "Warum 18 pt die Untergrenze ist und AutoFit eine Warnung statt einer Lösung.",
  durationMinutes: 10,
  learningGoals: [
    "Schriftgrößen aus dem Layout übernehmen statt einzeln zu setzen",
    "Die Untergrenze für lesbare Folienschrift einhalten",
    "Die automatische Textanpassung als Warnsignal lesen",
  ],
  steps: [
    {
      id: "autofit",
      title: "Die AutoFit-Falle",
      explanation: [
        "Wenn mehr Text in einen Platzhalter geschrieben wird, als hineinpasst, " +
          "verkleinert PowerPoint die Schrift automatisch. Das ist gut gemeint und in der " +
          "Wirkung schädlich, weil es das eigentliche Problem versteckt: Der Inhalt passt " +
          "nicht auf die Folie.",
        "Zwei Folgen hat das. Erstens wird der Text irgendwann unlesbar – im " +
          "Konferenzraum sitzt jemand in der letzten Reihe. Zweitens hat danach jede " +
          "Folie eine andere Schriftgröße, je nachdem, wie viel Text darauf steht. Die " +
          "Präsentation wirkt unruhig, ohne dass man den Grund benennen kann.",
        "Das kleine Symbol, das links unten am Platzhalter erscheint, sobald PowerPoint " +
          "verkleinert hat, ist deshalb kein Hinweis – es ist eine Warnung. Über dieses " +
          "Symbol lässt sich die automatische Anpassung auch abschalten: „Text nicht " +
          "automatisch anpassen“. Danach läuft zu viel Text sichtbar über den Rand hinaus, " +
          "was unangenehm aussieht und genau deshalb hilft: Man teilt die Folie.",
      ],
      keyPoints: [
        "Das Symbol am Platzhalter meldet, dass PowerPoint die Schrift verkleinert hat.",
        "Rechtsklick → Form formatieren → Textoptionen schaltet AutoFit ab.",
        "Verkleinerte Schrift ist ein Symptom, nicht die Lösung.",
      ],
    },
    {
      id: "untergrenze",
      title: "Die Untergrenze",
      explanation: [
        "Als Untergrenze für Text, den das Publikum lesen soll, haben sich 18 pt " +
          "eingebürgert. Bei Vorträgen in größeren Räumen sind eher 24 pt angebracht, für " +
          "Folientitel 32 bis 44 pt.",
        "Es gibt einen einfachen Test dafür, ohne den Raum zu kennen: Stell dich in " +
          "doppelter Bildschirmhöhe Abstand vor deinen Monitor. Was du von dort aus nicht " +
          "mühelos lesen kannst, ist auf dem Beamer zu klein.",
        "Ausgenommen sind bewusst kleine Elemente wie Quellenangaben unter einem " +
          "Diagramm. Die dürfen bei 10 bis 12 pt liegen – sie sind für die Nachlese da, " +
          "nicht für den Vortrag. Genau deshalb stehen sie auch in einem eigenen " +
          "Textfeld und nicht im Inhaltsplatzhalter.",
        "Die wichtigste Regel bleibt aber: Schriftgrößen werden nicht auf der einzelnen " +
          "Folie eingestellt. Sie kommen aus dem Layout. Wenn dir die Titel " +
          "durchgehend zu groß sind, ändere sie im Folienmaster – dann stimmen sie " +
          "überall auf einmal.",
      ],
      keyPoints: [
        "18 pt Untergrenze für Folientext, 24 pt bei größeren Räumen.",
        "Abstandstest: doppelte Bildschirmhöhe vom Monitor entfernt.",
        "Größen im Folienmaster ändern, nicht Folie für Folie.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass in keinem Textplatzhalter deiner Folien Schrift unter 18 pt " +
          "steht.",
        hint: "Markiere den Text und sieh in der Schriftgrößenanzeige nach. Steht dort nichts, ist der Text gemischt formatiert.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Die Schriftgröße im Textrahmen",
              "1.4",
              "Markiere den Text der Platzhalter und lies die Schriftgröße selbst ab.",
            );
          }

          const platzhalter = allShapes(snapshot).filter(
            (form) => isPlaceholder(form) && form.paragraphs.length > 0,
          );
          if (platzhalter.length === 0) {
            return fail(
              "In keinem Platzhalter steht Text.",
              "Schreibe zuerst Text in Titel und Inhaltsplatzhalter.",
            );
          }

          const zuKlein = platzhalter.filter(
            (form) => typeof form.font?.size === "number" && form.font.size < 18,
          );
          // Bei gemischt formatiertem Text liefert PowerPoint keine Größe. Das
          // ist kein Fehler, aber auch kein Nachweis – es wird offen benannt.
          const gemischt = platzhalter.filter((form) => form.font?.size === null);

          if (zuKlein.length > 0) {
            const kleinster = zuKlein.reduce((a, b) =>
              (a.font?.size ?? 99) <= (b.font?.size ?? 99) ? a : b,
            );
            return fail(
              `Auf Folie ${kleinster.slideIndex + 1} steht Text in ${kleinster.font?.size} pt.` +
                (zuKlein.length > 1 ? ` Insgesamt betrifft das ${zuKlein.length} Platzhalter.` : ""),
              "Statt die Schrift zu vergrößern, besser den Text kürzen oder die Folie teilen – dann passt die Größe aus dem Layout wieder.",
            );
          }

          if (gemischt.length > 0) {
            return fail(
              `${gemischt.length === 1 ? "Ein Platzhalter ist" : `${gemischt.length} Platzhalter sind`} gemischt formatiert, ` +
                `zum Beispiel auf Folie ${gemischt[0].slideIndex + 1}. PowerPoint gibt für solche Bereiche keine Schriftgröße aus, ` +
                "deshalb lässt sich die Untergrenze hier nicht belegen.",
              "Markiere den gesamten Platzhalterinhalt und setze ihn über „Start“ → „Zurücksetzen“ auf die Größe des Layouts zurück. Danach ist die Größe wieder eindeutig.",
            );
          }

          const kleinste = Math.min(
            ...platzhalter
              .map((form) => form.font?.size)
              .filter((groesse): groesse is number => typeof groesse === "number"),
          );
          return pass(
            `Der kleinste Platzhaltertext steht bei ${kleinste} pt – auch aus der letzten Reihe lesbar.`,
          );
        },
      },
    },
    {
      id: "hervorheben",
      title: "Hervorheben ohne Lärm",
      explanation: [
        "Auf einer Folie mit fünf Stichpunkten braucht es selten eine Hervorhebung – die " +
          "Struktur allein trägt schon. Wenn doch, dann sparsam und mit einem einzigen " +
          "Mittel.",
        "Fett funktioniert am zuverlässigsten, weil es auch aus der Entfernung wirkt. " +
          "Kursiv verliert auf dem Beamer fast seine Wirkung. Unterstreichen ist im Web " +
          "als Link belegt und wirkt auf Folien altmodisch. Farbe funktioniert, sofern " +
          "sie aus dem Design kommt und der Kontrast stimmt – dazu mehr im Modul " +
          "Barrierefreiheit.",
        "Das gilt auch für Großbuchstaben: EIN GANZER SATZ IN VERSALIEN liest sich " +
          "langsamer, weil die Wortumrisse verloren gehen, an denen das Auge sich " +
          "orientiert. Für ein einzelnes Wort ist das kein Problem, für eine Überschrift " +
          "schon.",
      ],
      keyPoints: [
        "Strg + Umschalt + F ist fett, Strg + Umschalt + K kursiv.",
        "Ein Hervorhebungsmittel pro Präsentation genügt – meistens fett.",
        "Versalien verlangsamen das Lesen, weil die Wortumrisse fehlen.",
      ],
      task: {
        instruction:
          "Beschränke dich in den Platzhaltern auf ein einziges Hervorhebungsmittel – " +
          "also nicht fett und kursiv und unterstrichen nebeneinander.",
        hint: "Unterstreichen ist auf Folien fast immer das erste, worauf man verzichten kann.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Die Schriftauszeichnung im Textrahmen",
              "1.4",
              "Sieh deine Folien selbst durch: Wird fett, kursiv und unterstrichen nebeneinander verwendet?",
            );
          }

          const platzhalter = allShapes(snapshot).filter(
            (form) => isPlaceholder(form) && form.paragraphs.length > 0,
          );
          if (platzhalter.length === 0) {
            return fail(
              "In keinem Platzhalter steht Text.",
              "Schreibe zuerst Text in die Platzhalter.",
            );
          }

          // Bei gemischter Formatierung liefert PowerPoint null. Gewertet wird
          // deshalb nur, was eindeutig gesetzt ist – das reicht, um ein zweites
          // oder drittes Mittel zu erkennen.
          const mittel: string[] = [];
          if (platzhalter.some((form) => form.font?.bold === true)) mittel.push("fett");
          if (platzhalter.some((form) => form.font?.italic === true)) mittel.push("kursiv");
          if (
            platzhalter.some(
              (form) => form.font?.underline && form.font.underline.toLowerCase() !== "none",
            )
          ) {
            mittel.push("unterstrichen");
          }

          if (mittel.length <= 1) {
            return pass(
              mittel.length === 0
                ? "Es ist keine zusätzliche Auszeichnung im Einsatz – die Struktur trägt die Folie allein."
                : `Als Hervorhebung wird ausschließlich ${mittel[0]} verwendet.`,
            );
          }

          return fail(
            `Es sind ${mittel.length} Hervorhebungsmittel gleichzeitig im Einsatz: ${mittel.join(", ")}.`,
            "Entscheide dich für eines – auf Folien wirkt fett am zuverlässigsten. Kursiv und Unterstreichen gehen auf dem Beamer ohnehin unter.",
          );
        },
      },
    },
  ],
};
